import { useState } from 'react';
import {
  calculatePaletteFitness,
  getUseCaseOptions,
  UseCaseType,
  FitnessScoreResult,
} from '../lib/paletteScoring';
import { Activity, AlertCircle, CheckCircle, Info, TrendingUp, XCircle } from 'lucide-react';

interface FitnessScorePanelProps {
  colors: string[];
}

export function FitnessScorePanel({ colors }: FitnessScorePanelProps) {
  const [selectedUseCase, setSelectedUseCase] = useState<UseCaseType>('dark-ui-aa');
  const [result, setResult] = useState<FitnessScoreResult | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const useCaseOptions = getUseCaseOptions();

  const handleCalculate = () => {
    if (colors.length === 0) {
      return;
    }
    const fitnessResult = calculatePaletteFitness(colors, selectedUseCase);
    setResult(fitnessResult);
    setShowDetails(true);
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'B':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'C':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'D':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'F':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 80) return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (score >= 60) return <AlertCircle className="w-4 h-4 text-yellow-600" />;
    return <XCircle className="w-4 h-4 text-red-600" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-purple-700">
        <TrendingUp className="w-5 h-5" />
        <h3 className="font-semibold">Palette Fitness Scoring</h3>
      </div>

      {colors.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>Add colors to your palette to calculate fitness scores.</p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Use Case
          </label>
          <select
            value={selectedUseCase}
            onChange={(e) => setSelectedUseCase(e.target.value as UseCaseType)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {useCaseOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label} - {option.description}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleCalculate}
          disabled={colors.length === 0}
          className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          <Activity className="w-4 h-4" />
          Calculate Fitness Score
        </button>
      </div>

      {result && showDetails && (
        <div className="space-y-4 mt-6">
          {/* Overall Score Card */}
          <div className={`border-2 rounded-lg p-6 ${getGradeColor(result.grade)}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-lg font-bold">Overall Score</h4>
              <div className="text-4xl font-bold">{result.grade}</div>
            </div>
            <div className="text-2xl font-semibold mb-2">
              {result.overallScore.toFixed(1)}/100
            </div>
            <p className="text-sm opacity-90">{result.summary}</p>
          </div>

          {/* Individual Metrics */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Metric Breakdown
            </h4>

            {Object.entries(result.metrics).map(([key, metric]) => (
              <div key={key} className="border-l-4 border-gray-200 pl-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getScoreIcon(metric.score)}
                    <span className="font-medium text-gray-900">{metric.label}</span>
                  </div>
                  <span className={`font-semibold ${getScoreColor(metric.score)}`}>
                    {metric.score.toFixed(0)}/100
                  </span>
                </div>

                <p className="text-sm text-gray-600">{metric.description}</p>

                {metric.details.length > 0 && (
                  <div className="text-xs text-gray-500 space-y-1">
                    {metric.details.map((detail, idx) => (
                      <div key={idx}>• {detail}</div>
                    ))}
                  </div>
                )}

                {metric.recommendations.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800 space-y-1">
                    {metric.recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-1">
                        <span className="text-blue-600">→</span>
                        <span>{rec}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Strengths */}
          {result.strengths.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Strengths
              </h4>
              <ul className="space-y-1 text-sm text-green-800">
                {result.strengths.map((strength, idx) => (
                  <li key={idx}>✓ {strength}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Weaknesses */}
          {result.weaknesses.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Areas for Improvement
              </h4>
              <ul className="space-y-1 text-sm text-red-800">
                {result.weaknesses.map((weakness, idx) => (
                  <li key={idx}>✗ {weakness}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Actionable Recommendations */}
          {result.actionableRecommendations.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Actionable Recommendations
              </h4>
              <ul className="space-y-2 text-sm text-purple-800">
                {result.actionableRecommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">{idx + 1}.</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
